import React, { useEffect, useState } from 'react'
import TagTable from 'components/permissions/views/tag/components/tagTable'
import { useApplicationStore } from 'hooks/application'
import { useProject } from 'hooks/useProject'
import api from 'helpers/api'
import { IAccessPolicy, ITableWithCells } from 'types'
import { UserRoles, AccessAttributeKinds } from 'app-constants'
import { Triangle } from 'components/icons'

interface Props {
  display: boolean
  policies: IAccessPolicy[]
  onCreatePolicy: (
    role: UserRoles,
    attributeKind: AccessAttributeKinds,
    attributeId?: string,
    tagReferenceId?: string
  ) => void
  onDeletePolicy: (policy: IAccessPolicy) => void
  excludedPermissionCategories?: UserRoles[]
  onBulkSetPolicies?: (
    role: UserRoles,
    attributeKind: AccessAttributeKinds,
    attributeIds?: string[],
    tagReferenceIds?: string[]
  ) => void
}

const getAllTableTagsPolicy = (policies: IAccessPolicy[], tableId: string, role: UserRoles) => {
  const allTableTagsPolicy = policies.find((policy) => {
    if (
      policy.accessAttribute?.kind === AccessAttributeKinds.ALL_TABLE_TAGS &&
      policy.accessAttribute?.documentTable?.publicId === tableId &&
      policy.role === role
    ) {
      return true
    }
  })
  return allTableTagsPolicy
}

const TagPermissions: React.FC<Props> = ({
  display,
  onCreatePolicy,
  policies,
  onDeletePolicy,
  excludedPermissionCategories,
  onBulkSetPolicies
}) => {
  const [selectedLevel, setSelectedLevel] = useState<string>('none')
  const { project } = useProject()
  const [tagTables, setTagTables] = useState<ITableWithCells[]>([])
  const { displayErrorMessage } = useApplicationStore()

  const permissionLevels = [
    { id: 'view', level: UserRoles.VIEWER, name: 'View Permissions', icon: 'new-eye', viewbox: '0 0 512 512' },
    {
      id: 'contribute',
      level: UserRoles.CONTRIBUTOR,
      name: 'Contribute Permissions',
      icon: 'pencil',
      viewbox: '0 0 512 512'
    },
    { id: 'owner', level: UserRoles.OWNER, name: 'Owner Permissions', icon: 'owner', viewbox: '0 0 128 128' }
  ].filter(
    (permissionLevel) => !excludedPermissionCategories || !excludedPermissionCategories.includes(permissionLevel.level)
  )

  const fetchProjectTags = async () => {
    try {
      const response = await api.getProjectTags(project.publicId)
      setTagTables(response.data.sort((a, b) => (a.name > b.name ? 1 : -1)))
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const handleAddTag = (tagReferenceId: string, role: UserRoles) => {
    onCreatePolicy(role, AccessAttributeKinds.TAG, undefined, tagReferenceId)
  }

  const handleDeleteTag = (tagId: string, role: UserRoles) => {
    const policy = policies.find((p) => {
      if (
        p.accessAttribute?.kind === AccessAttributeKinds.TAG &&
        p.accessAttribute?.tag?.referencePublicId === tagId &&
        p.role === role
      ) {
        return true
      }
    })
    if (!policy) return
    onDeletePolicy(policy)
  }

  const handleAddAllTags = (tableId: string, role: UserRoles) => {
    onCreatePolicy(role, AccessAttributeKinds.ALL_TABLE_TAGS, tableId)
  }

  const handleDeleteAllTags = (tableId: string, role: UserRoles) => {
    const policy = getAllTableTagsPolicy(policies, tableId, role)
    if (!policy) return
    onDeletePolicy(policy)
  }

  useEffect(() => {
    fetchProjectTags()
  }, [])

  const handleBulkSetTags = async (role: UserRoles, tagReferenceIds: string[]) => {
    if (!onBulkSetPolicies) return
    onBulkSetPolicies(role, AccessAttributeKinds.TAG, undefined, tagReferenceIds)
  }

  return (
    <div className={`overflow-y-auto ${display ? 'block h-[95%]' : 'hidden h-0'}`}>
      {permissionLevels.map((permissionLevel, permissionIndex) => {
        const active = selectedLevel === permissionLevel.id

        // If there are polices based on tags for this role
        const hasPolicies = policies.find(
          (p) =>
            p.role === permissionLevel.level &&
            (p.accessAttribute?.kind === AccessAttributeKinds.ALL_TABLE_TAGS ||
              p.accessAttribute?.kind === AccessAttributeKinds.TAG)
        )
        return (
          <div
            key={permissionIndex}
            className={`flex flex-col cursor-pointer select-none rounded-lg box-border transition-all border border-divider hover:shadow-heavy hover:-translate-x-px hover:-translate-y-px p-6 my-2.5 mx-[5px] ${active ? 'min-h-[200px]' : 'min-h-[90px]'}`}
          >
            <div
              className="flex h-full content-center"
              onClick={() => (active ? setSelectedLevel('none') : setSelectedLevel(permissionLevel.id))}
            >
              <div className="flex flex-col justify-center">
                <div className="font-bold mb-[5px]">
                  {permissionLevel.name}
                  {hasPolicies ? (
                    <span className="bg-green rounded p-[5px] ml-2.5">Active</span>
                  ) : (
                    <span className="bg-red rounded p-[5px] ml-2.5">Inactive</span>
                  )}
                </div>
                <div className="w-full truncate">
                  Set the combination of tags that allow users {' ' + permissionLevel.id + ' '} access. <br />A user
                  will have to have at least one tag from each category below to be given access.
                </div>
              </div>
              <div className="flex items-center ml-auto h-full">
                <Triangle type={!active ? 'right' : 'down'} />
              </div>
            </div>
            <div
              className={`relative flex flex-col transition-all mt-[15px] max-w-full ${active ? 'opacity-100 visible h-full' : 'opacity-0 invisible h-0'}`}
            >
              {active &&
                tagTables.map((tagTable, index) => {
                  return (
                    <TagTable
                      key={index}
                      policies={policies}
                      tags={tagTable}
                      tableId={tagTable.publicId}
                      onTagAdd={(tagId) => handleAddTag(tagId, permissionLevel.level)}
                      onTagDelete={(tagId) => handleDeleteTag(tagId, permissionLevel.level)}
                      role={permissionLevel.level}
                      onAllTagsAdd={handleAddAllTags}
                      onAllTagsDelete={handleDeleteAllTags}
                      onBulkTagAdd={(tagIds) => {
                        handleBulkSetTags(permissionLevel.level, tagIds)
                      }}
                    />
                  )
                })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TagPermissions
